import React, { useMemo, useState } from 'react';
import {
    Avatar,
    Box,
    Button,
    Chip,
    Collapse,
    Divider,
    Grid,
    IconButton,
    Paper,
    Stack,
    Typography,
    alpha,
    useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import DeleteIcon from '@mui/icons-material/Delete';

import { useLocation, useNavigate } from 'react-router-dom';
import { useAuditHistoryQuery } from '@features/redux/apis/audit';
import { AppRoutes } from '@consts/routes';
import dayjs from 'dayjs';
import { decodeTime } from 'ulid';
import { AuditLogTable } from './AuditLogTable';

/* ------------------------------------------------------------------ */
/* helpers                                                            */
/* ------------------------------------------------------------------ */

type AuditOperation = 'CREATE' | 'UPDATE' | 'DELETE';

const isEmpty = (value: unknown): boolean => {
    return (
        value === undefined ||
        value === null ||
        (typeof value === 'object' && value !== null && Object.keys(value).length === 0)
    );
};

const opColor = (theme: any, op: AuditOperation) => {
    switch (op) {
        case 'CREATE':
            return theme.palette.success.main;
        case 'UPDATE':
            return theme.palette.info.main;
        case 'DELETE':
            return theme.palette.error.main;
        default:
            return theme.palette.text.secondary;
    }
};

const opIcon = (op: AuditOperation) => {
    switch (op) {
        case 'CREATE':
            return <AddIcon fontSize='small' />;
        case 'UPDATE':
            return <ChangeCircleIcon fontSize='small' />;
        case 'DELETE':
            return <DeleteIcon fontSize='small' />;
    }
};

interface OperationChipProps {
    op: AuditOperation;
}

const OperationChip: React.FC<OperationChipProps> = ({ op }) => {
    const theme = useTheme();
    return (
        <Chip
            size='small'
            icon={opIcon(op)}
            label={op.charAt(0) + op.slice(1).toLowerCase()}
            variant='outlined'
            sx={{
                borderColor: opColor(theme, op),
                color: opColor(theme, op),
                '& .MuiChip-icon': { color: opColor(theme, op) },
                fontWeight: 600,
                letterSpacing: 0.25,
            }}
        />
    );
};

const getInitials = (email?: string) => email?.[0]?.toUpperCase() ?? '?';

const pretty = (v: unknown) =>
    v === null || v === undefined ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v);

/* ------------------------------------------------------------------ */
/* Delta table (inside card)                                          */
/* ------------------------------------------------------------------ */

interface DeltaTableProps {
    delta: Record<string, { before?: unknown; after?: unknown }>;
}

const DeltaTable: React.FC<DeltaTableProps> = ({ delta }) => {
    const theme = useTheme();
    return (
        <Grid container spacing={0.5}>
            <Grid item xs={3} md={2}>
                <Typography variant='body2' fontWeight={600}>
                    Field
                </Typography>
            </Grid>
            <Grid item xs={2}>
                <Typography variant='body2' fontWeight={600}>
                    Op
                </Typography>
            </Grid>
            <Grid item xs={3.5} md={4}>
                <Typography variant='body2' fontWeight={600} color='error.main'>
                    Before
                </Typography>
            </Grid>
            <Grid item xs={3.5} md={4}>
                <Typography variant='body2' fontWeight={600} color='success.main'>
                    After
                </Typography>
            </Grid>

            {Object.entries(delta)
                .filter(([, deltaData]) => {
                    const before = deltaData?.before;
                    const after = deltaData?.after;
                    // Only show entries where at least one value is not empty
                    return !isEmpty(before) || !isEmpty(after);
                })
                .map(([field, deltaData]) => {
                    const before = deltaData?.before;
                    const after = deltaData?.after;
                    const changed = JSON.stringify(before) !== JSON.stringify(after);
                    const beforeBg = changed ? alpha(theme.palette.error.light, 0.15) : 'transparent';
                    const afterBg = changed ? alpha(theme.palette.success.light, 0.15) : 'transparent';

                    return (
                        <React.Fragment key={field}>
                            <Grid item xs={3} md={2}>
                                <Typography variant='body2'>{field}</Typography>
                            </Grid>
                            <Grid item xs={2}>
                                <Typography variant='body2'>UPDATE</Typography>
                            </Grid>
                            <Grid item xs={3.5} md={4} sx={{ bgcolor: beforeBg }}>
                                <Typography
                                    variant='body2'
                                    color='error.main'
                                    sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                                    {pretty(before)}
                                </Typography>
                            </Grid>
                            <Grid item xs={3.5} md={4} sx={{ bgcolor: afterBg }}>
                                <Typography
                                    variant='body2'
                                    color='success.main'
                                    sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                                    {pretty(after)}
                                </Typography>
                            </Grid>
                        </React.Fragment>
                    );
                })}
        </Grid>
    );
};

/* ------------------------------------------------------------------ */
/* Audit-card (collapsible)                                           */
/* ------------------------------------------------------------------ */

interface AuditCardProps {
    audit: {
        email: string;
        op: AuditOperation;
        ts?: string; // add ts in your API if available
        delta: Record<string, { before: unknown; after: unknown }>;
    };
    defaultOpen?: boolean;
    elevation?: number;
}

const AuditCard: React.FC<AuditCardProps> = ({ audit, elevation, defaultOpen = false }) => {
    const [open, setOpen] = useState(defaultOpen);
    const theme = useTheme();

    return (
        <Paper
            elevation={elevation ?? (open ? 3 : 1)}
            sx={{
                p: 2,
                borderRadius: 2,
                transition: theme.transitions.create(['box-shadow', 'transform'], {
                    duration: theme.transitions.duration.shortest,
                }),
                ...(open && { transform: 'scale(1.01)' }),
            }}>
            <Stack direction='row' spacing={2} alignItems='center'>
                <Avatar sx={{ width: 36, height: 36 }}>{getInitials(audit.email)}</Avatar>

                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant='subtitle2' noWrap fontWeight={700}>
                        {audit.email}
                    </Typography>
                    {audit.ts && (
                        <Typography variant='caption' color='text.secondary' noWrap>
                            {dayjs(decodeTime(audit.ts.split('ts#')[1])).format('MMM D YYYY, h:mm A')}
                        </Typography>
                    )}
                </Box>

                <OperationChip op={audit.op} />

                <IconButton size='small' onClick={() => setOpen(!open)} sx={{ ml: 1 }}>
                    {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </Stack>

            <Collapse in={open} timeout='auto' unmountOnExit>
                <Divider sx={{ my: 2 }} />
                <DeltaTable delta={audit.delta} />
            </Collapse>
        </Paper>
    );
};

/* ------------------------------------------------------------------ */
/* Top-level component                                                */
/* ------------------------------------------------------------------ */

export interface AuditTimelineProps {
    entityPk: string;
}

export const AuditTimeline: React.FC<AuditTimelineProps> = ({ entityPk }) => {
    const navigate = useNavigate();
    const { data = [], isFetching } = useAuditHistoryQuery({ entityPk }, { skip: !entityPk });

    const currentRoute = useLocation();
    const isAuditPage = currentRoute.pathname.includes('audit');

    const sorted = useMemo(() => [...data].reverse(), [data]);

    return (
        <Box mt={2} sx={{ width: '100%' }}>
            <Box display={'flex'} flexDirection={'row'} gap={1} flex={1} width={'100%'}>
                {sorted[0] && !isAuditPage ? (
                    <Box flex={1} width={'100%'} gap={1} flexDirection={'column'} display={'flex'}>
                        <AuditCard key={`audit-${0}`} audit={sorted[0] as any} elevation={0} />
                    </Box>
                ) : isAuditPage ? (
                    <Box sx={{ width: '100%' }}>
                        <Typography
                            variant='h1'
                            sx={{
                                fontSize: '24px',
                                mb: 3,
                                fontWeight: 700,
                                color: 'black',
                            }}>
                            Audit Log
                        </Typography>
                        <AuditLogTable audits={sorted} pageSize={15} />
                    </Box>
                ) : (
                    <Typography sx={{ m: 1 }}>No audit history found.</Typography>
                )}

                {data.length >= 1 && !isAuditPage && (
                    <Box textAlign='center' display={'flex'} alignItems={'center'} justifyContent={'center'}>
                        <Button
                            variant='text'
                            sx={{ textDecoration: 'underline', textTransform: 'none' }}
                            size='small'
                            onClick={() => {
                                const [prefix, rawId] = entityPk.split('#');
                                let type = prefix.toLowerCase();
                                if (type === 'station') type = 'system';

                                const id = rawId ?? '';

                                navigate(
                                    `${AppRoutes.AuditTrail}?type=${type}&id=${id}&entityPk=${encodeURIComponent(
                                        entityPk
                                    )}`
                                );
                            }}>
                            View All
                        </Button>
                    </Box>
                )}
            </Box>
        </Box>
    );
};
